import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import clsx from 'clsx'
import { AlertOctagon, ChevronRight } from 'lucide-react'
import { useSession } from '../auth/useSession'
import { pollWhileVisible } from '../lib/pollWhileVisible'
import { EmptyState } from '../cc/EmptyState'
import { FilterChips } from '../cc/FilterChips'
import { NetradyneCameraButton } from '../cc/NetradyneCameraButton'
import { KpiCard } from '../cc/KpiCard'
import { KpiStrip } from '../cc/KpiStrip'
import { Panel } from '../cc/Panel'
import { SearchBox } from '../cc/SearchBox'
import { SidePanel } from '../cc/SidePanel'
import { EYEBROW } from '../cc/tokens'
import { ago, clock, dateTime } from '../lib/format'
import { requireSupervisor } from '../lib/supervisor'
import { defaultSurfaceFor, fleetPills, pathFor, surfacePills, type FleetId } from '../cc/fleetNav'
import { TopBar } from '../cc/TopBar'
import { BarChart, type BarPoint, type BarSeries } from '../reports/charts/Charts'
import { EimAlertDetail } from './EimAlertDetail'
import { EimBreakdownForm } from './EimBreakdownForm'
import { EimPortsMap } from './EimPortsMap'
import { EIM_STATUS_LABEL, EIM_STATUS_STYLE, eimStatusKey, type EimStatusKey } from './statusStyle'
import {
  ackEimAlert,
  actOnEimBreakdown,
  dismissEimMoving,
  fetchEimAlertAcks,
  fetchEimBreakdownCodes,
  fetchEimBreakdownReport,
  fetchEimBreakdowns,
  fetchEimIdleAlertList,
  fetchEimIdleAlertReport,
  fetchEimIdleEpisodes,
  fetchEimLive,
  fetchEimOverheatAlertList,
  fetchEimOverheatAlertReport,
  fetchEimOverheatEpisodesTodaySite,
  fetchEimSummary,
  fetchEimTrend,
  formatIdlePowerW,
  GTI_LOW_SOC_PCT,
  idleMinutes,
  IDLE_ALERT_THRESHOLD_MIN,
  isBatteryOverheatAlert,
  isIdleAlert,
  isLowSoc,
  unackEimAlert,
  type EimAlertAck,
  type EimAlertKind,
  type EimBreakdown,
  type EimBreakdownCode,
  type EimBreakdownReportKind,
  type EimBreakdownReportRow,
  type EimIdleAlertDailyRow,
  type EimIdleAlertListRow,
  type EimIdleAlertVehicleRow,
  type EimIdleRow,
  type EimOverheatAlertDailyRow,
  type EimOverheatAlertListRow,
  type EimOverheatAlertVehicleRow,
  type EimOverheatEpisode,
  type EimSite,
  type EimSummary,
  type EimTrendRow,
  type EimVehicle,
} from './useEimApi'

function sessionDisplayName(session: ReturnType<typeof useSession>): string | undefined {
  return session.status === 'signed-in' ? session.session.name || session.session.email : undefined
}

const SITE_LABEL: Record<EimSite, string> = { gti: 'GTI', jm_baxi: 'JM Baxi' }
const SITE_COLOR: Record<EimSite, string> = { gti: '#0369A1', jm_baxi: '#B45309' }

// Same chip set every other fleet shell's vehicle grid offers (Ultratech/MBMT/Nagpur) --
// one chip per eimStatusKey() value, OFFLINE last regardless of label order so a quiet
// vehicle is never mistaken for one that just hasn't reported this tick.
const FLEET_FILTERS: { id: 'all' | EimStatusKey; label: string }[] = [
  { id: 'all', label: 'ALL' },
  { id: 'moving', label: 'MOVING' },
  { id: 'idle', label: 'IDLE' },
  { id: 'parked', label: 'PARKED' },
  { id: 'charging', label: 'CHARGING' },
  { id: 'alert', label: 'FLAGGED' },
  { id: 'overheat', label: 'OVERHEAT' },
  { id: 'breakdown', label: 'DOWN' },
  { id: 'offline', label: 'OFFLINE' },
]

/** One shared bar for both sites now (see IDLE_ALERT_THRESHOLD_MIN's own comment) -- no
 *  per-site branching left to spell out here. */
const THRESHOLD_LABEL = `${IDLE_ALERT_THRESHOLD_MIN}m+`

function fmtAgo(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000))
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m`
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`
}

/**
 * One vehicle tile. Not VehicleCard (cc/) -- that component's fields (load, LR, trailer,
 * TIMS event) are Ultratech-truck-corridor specific and none apply here: GTI/JM_BAXI have
 * no TIMS register, no geofence corridor, just BMS/GPS telemetry. Forcing SOC and idle
 * draw into VehicleCard's load/event slots would show something dishonest in a field
 * labelled for something else.
 */
function EimVehicleTile({ v, now, openBreakdown, selected, onSelect, onReportBreakdown }: {
  v: EimVehicle; now: number
  openBreakdown: EimBreakdown | null
  selected: boolean
  onSelect: () => void
  onReportBreakdown: () => void
}): JSX.Element {
  const key = eimStatusKey(v, now, () => openBreakdown != null)
  const style = EIM_STATUS_STYLE[key]
  const idleMin = key === 'offline' ? null : idleMinutes(v, now)
  const lowSoc = isLowSoc(v)
  // NOT a fault indicator -- these are raw vendor bitmasks with no known bit reference
  // (see useEimApi.ts's own comment). Shown as codes, in the same muted ink as every
  // other number on the tile, precisely so it does not read as a status this dashboard
  // is not in a position to claim.
  const codes = [v.vcu_fault_level, v.vcu_strategy_fault_level, v.mcu_error_level, v.motor_fault]
  const hasCodes = codes.some((c) => c != null && c !== 0)

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect() } }}
      aria-pressed={selected}
      className={clsx(
        'flex h-full flex-col rounded-[12px] border p-3 text-left transition-all cursor-pointer',
        'hover:-translate-y-px hover:shadow-[0_2px_6px_rgba(15,23,42,0.08)] motion-reduce:hover:translate-y-0',
        'focus-visible:outline-none',
        selected && 'ring-2 ring-[#2563EB]',
      )}
      style={{ backgroundColor: style.bg, borderColor: style.border }}
    >
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span
            className={clsx('h-2 w-2 shrink-0 rounded-full', (key === 'alert' || key === 'overheat') && 'motion-safe:animate-pulse')}
            style={{ backgroundColor: style.hex }}
          />
          <span className="font-mono text-[13px] font-bold tabular-nums text-[#0F172A]">{v.vehicle_id}</span>
          <span className="ml-auto" onClick={(e) => e.stopPropagation()}>
            <NetradyneCameraButton site={v.site} vehicleNumber={v.vehicle_id} />
          </span>
          <span className="text-[10px] font-medium uppercase tracking-wide text-[#94A3B8]">
            {SITE_LABEL[v.site]}
          </span>
        </div>
        <div className="mt-1 text-[13px] font-medium" style={{ color: style.text }}>
          {EIM_STATUS_LABEL[key]}
          {key !== 'offline' && (
            <span className="ml-1.5 font-mono text-[11px] text-[#94A3B8]">{fmtAgo(now - new Date(v.polled_ts).getTime())} ago</span>
          )}
        </div>
        <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-[#64748B]">
          <span>
            SOC{' '}
            <b className="font-mono tabular-nums" style={{ color: lowSoc ? '#9F1239' : '#0F172A' }}>
              {v.soc != null ? `${v.soc.toFixed(0)}%` : '—'}
            </b>
          </span>
          <span>Speed <b className="font-mono tabular-nums text-[#0F172A]">{v.gps_speed != null ? v.gps_speed.toFixed(0) : '—'}</b> km/h</span>
          {idleMin != null ? (
            <span className="col-span-2">
              {key === 'parked' ? 'Parked' : 'Idle'} <b className="font-mono tabular-nums text-[#0F172A]">{idleMin}m</b>
              {v.idle_power_w != null ? (
                <> · draw <b className="font-mono tabular-nums text-[#0F172A]">{formatIdlePowerW(v.idle_power_w)}</b></>
              ) : null}
            </span>
          ) : null}
          {lowSoc ? (
            <span className="col-span-2 font-semibold text-[#9F1239]">Low battery -- below {GTI_LOW_SOC_PCT}%</span>
          ) : null}
          {hasCodes ? (
            <span className="col-span-2 text-[#94A3B8]" title="Raw vendor codes -- meaning not documented">
              codes <span className="font-mono tabular-nums">{codes.map((c) => c ?? '—').join('/')}</span>
            </span>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onReportBreakdown() }}
        className={
          'mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 text-[11px] font-semibold transition-colors '
          + (openBreakdown
            ? 'border-[rgba(244,63,94,0.30)] bg-[rgba(244,63,94,0.08)] text-[#9F1239] hover:bg-[rgba(244,63,94,0.14)]'
            : 'border-[rgba(15,23,42,0.10)] text-[#64748B] hover:border-[rgba(244,63,94,0.30)] hover:text-[#9F1239]')
        }
      >
        <AlertOctagon size={11} />
        {openBreakdown ? 'Update breakdown' : 'Mark breakdown'}
      </button>
    </div>
  )
}

function matchesVehicleQuery(v: EimVehicle, q: string): boolean {
  return v.vehicle_id.toLowerCase().includes(q)
}

function SiteToggle({ value, onChange }: { value: EimSite | 'all'; onChange: (v: EimSite | 'all') => void }): JSX.Element {
  const options: Array<{ id: EimSite | 'all'; label: string }> = [
    { id: 'all', label: 'All sites' },
    { id: 'gti', label: 'GTI' },
    { id: 'jm_baxi', label: 'JM Baxi' },
  ]
  return (
    <div className="flex items-center gap-0.5 rounded-full bg-[#EEF2F7] p-0.5">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          aria-current={value === o.id ? 'page' : undefined}
          className={clsx(
            'rounded-full px-3 py-1 text-[11px] font-medium transition-colors',
            value === o.id
              ? 'bg-white text-[#0F172A] shadow-[0_1px_2px_rgba(15,23,42,0.10)]'
              : 'text-[#64748B] hover:text-[#0F172A]',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function LiveView({ site, onSite }: { site: EimSite | 'all'; onSite: (s: EimSite | 'all') => void }): JSX.Element {
  // The audit trail should name the actual signed-in operator, not a self-declared
  // per-browser string (supervisor.ts's own requireSupervisor) -- that mechanism predates
  // this console having real sign-in and is now only a fallback for the rare case nobody
  // is signed in (shared/kiosk terminal), so a stale or joke name typed into its one-time
  // prompt (e.g. "test") can no longer get stuck forever once a real session exists.
  const session = useSession()
  const signedInName = sessionDisplayName(session)
  const [vehicles, setVehicles] = useState<EimVehicle[]>([])
  // Today's (IST) BATTERY_OVERHEAT episodes per vehicle -- SPEC.md 2.5: lets a cleared
  // episode keep showing in the alert panel for the rest of the day, not just while the
  // live tile still flags it. ONE fleet-level request, folded into the existing 10s
  // refresh() cycle below (adversarial review, HIGH -- a per-vehicle-fetch-once design
  // fired ~66 parallel requests on page load AND never refreshed, so a cleared-while-
  // the-page-was-open episode could never appear -- this worktree's own CLAUDE.md "no
  // new recurring poll loop" rule is why this rides the existing cycle, not a new one).
  const [overheatEpisodesByVehicle, setOverheatEpisodesByVehicle] = useState<Record<string, EimOverheatEpisode[]>>({})
  const [summary, setSummary] = useState<EimSummary | null>(null)
  const [breakdowns, setBreakdowns] = useState<EimBreakdown[]>([])
  const [breakdownCodes, setBreakdownCodes] = useState<Record<EimSite, EimBreakdownCode[]>>({ gti: [], jm_baxi: [] })
  const [breakdownFormFor, setBreakdownFormFor] = useState<{ site: EimSite; vehicleId: string } | null>(null)
  const [acks, setAcks] = useState<EimAlertAck[]>([])
  const [alertDetailFor, setAlertDetailFor] = useState<{ v: EimVehicle; kind: EimAlertKind } | null>(null)
  // Alert row click used to open EimAlertDetail (acknowledge); the triage app owns
  // after-actions now, so it flies the map to the vehicle instead. mapPanelRef mirrors
  // StarShell.tsx's LiveView -- bring the map into view on a short viewport.
  const [focusVehicle, setFocusVehicle] = useState<{ site: EimSite; vehicleId: string } | null>(null)
  const mapPanelRef = useRef<HTMLDivElement | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [now, setNow] = useState(() => Date.now())
  const [filter, setFilter] = useState<'all' | EimStatusKey>('all')
  const [query, setQuery] = useState('')

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  // Guards against the 10s interval below firing a fresh round of 4 parallel fetches on
  // top of a round still in flight -- see BusOpsShell.tsx's own refreshInFlightRef for
  // the incident this class of bug caused there. Keyed by site (not a bare boolean) so
  // a switch to a different site is never blocked by a previous site's call still in
  // flight -- a bare boolean guard here silently dropped the new site's fetch until the
  // next 10s poll tick, which is exactly the "noticeable delay on site switch" QA bug.
  // activeSiteRef mirrors the latest `site` prop (updated every render, not just on
  // fetch completion) so a stale in-flight call that resolves AFTER a further switch
  // never overwrites the UI with the wrong site's numbers.
  const inFlightSiteRef = useRef<EimSite | 'all' | null>(null)
  const activeSiteRef = useRef(site)
  activeSiteRef.current = site

  const refresh = useCallback(async () => {
    const mySite = site
    if (inFlightSiteRef.current === mySite) return
    inFlightSiteRef.current = mySite
    // All 4 fire immediately, same as the single Promise.all this replaces (the overlap
    // guard's own call-count tests depend on that) -- only the awaiting/error-handling is
    // split into isolated groups, so a department without the alerts/breakdown_reports
    // grant 403ing the latter two can't blank the vehicle list/map that resolved fine.
    // Mirrors NagpurBusOpsShell's refresh().
    const livePromise = fetchEimLive(mySite)
    const summaryPromise = fetchEimSummary(mySite)
    const breakdownsPromise = fetchEimBreakdowns(mySite, true)
    const acksPromise = fetchEimAlertAcks(mySite)
    // 'all' has no combined-site read endpoint (same shape fetchEimAlertAcks' own 'all'
    // handling already uses) -- fetch both sites in parallel and merge.
    const overheatEpisodesPromise = Promise.all(
      (mySite === 'all' ? (['gti', 'jm_baxi'] as const) : [mySite]).map((s) => fetchEimOverheatEpisodesTodaySite(s)),
    ).then((lists) => lists.flat())
    try {
      const [v, s] = await Promise.all([livePromise, summaryPromise])
      if (activeSiteRef.current === mySite) {
        setVehicles(v)
        setSummary(s)
        setErr(null)
      }
    } catch (e) {
      if (activeSiteRef.current === mySite) setErr(e instanceof Error ? e.message : 'Failed to load Ports telemetry')
    }
    try {
      const b = await breakdownsPromise
      if (activeSiteRef.current === mySite) setBreakdowns(b)
    } catch {
      // No breakdown_reports grant (or a transient failure) -- leave the panel's own
      // previous/empty state rather than blanking the vehicle list/map above.
    }
    try {
      const a = await acksPromise
      if (activeSiteRef.current === mySite) setAcks(a)
    } catch {
      // Same reasoning as breakdowns above, for the alerts grant.
    }
    try {
      const episodes = await overheatEpisodesPromise
      if (activeSiteRef.current === mySite) {
        const byVehicle: Record<string, EimOverheatEpisode[]> = {}
        // Keyed by `${site}:${vehicle_id}`, not vehicle_id alone (adversarial review,
        // LOW -- round 2) -- db/schema_eim.sql's own comment on PORT_BREAKDOWN_SITES
        // documents the same plate appearing on both GTI and JM_BAXI accounts, which a
        // bare vehicle_id key would silently cross-wire in 'all' mode.
        for (const ep of episodes) (byVehicle[`${ep.site}:${ep.vehicle_id}`] ??= []).push(ep)
        setOverheatEpisodesByVehicle(byVehicle)
      }
    } catch {
      // Same reasoning as breakdowns/acks above -- leave the panel's previous state.
    }
    if (inFlightSiteRef.current === mySite) inFlightSiteRef.current = null
    if (activeSiteRef.current === mySite) setLoaded(true)
  }, [site])

  useEffect(() => {
    // Once, not polled -- static per site for the session, same reasoning STAR's own
    // fetchStarBreakdownCodes uses. Both sites fetched together regardless of the
    // current site filter so switching the toggle never has to wait on a second round
    // trip before the form can show real fault codes.
    Promise.all([fetchEimBreakdownCodes('gti'), fetchEimBreakdownCodes('jm_baxi')])
      .then(([gti, jm_baxi]) => setBreakdownCodes({ gti, jm_baxi }))
      .catch(() => setBreakdownCodes({ gti: [], jm_baxi: [] }))
  }, [])

  const quickResolve = useCallback(async (bdSite: EimSite, breakdownId: number) => {
    try {
      await actOnEimBreakdown(bdSite, breakdownId, 'resolve', { by: 'operator' })
      void refresh()
    } catch {
      // Silent -- the panel's own next 10s poll reconciles either way, and a failed
      // quick-resolve leaves the breakdown open (safe default).
    }
  }, [refresh])

  const dismissMoving = useCallback(async (bdSite: EimSite, breakdownId: number) => {
    try {
      await dismissEimMoving(bdSite, breakdownId)
      void refresh()
    } catch {
      // Same silent-and-reconcile-next-poll reasoning as quickResolve above.
    }
  }, [refresh])

  const breakdownByVehicle = useMemo(() => {
    const m = new Map<string, EimBreakdown>()
    for (const b of breakdowns) m.set(`${b.site}:${b.vehicle_id}`, b)
    return m
  }, [breakdowns])
  const isDown = useCallback(
    (vSite: EimSite, vehicleId: string) => breakdownByVehicle.has(`${vSite}:${vehicleId}`),
    [breakdownByVehicle],
  )

  const filteredVehicles = useMemo(() => {
    const q = query.trim().toLowerCase()
    return vehicles
      .filter((v) => filter === 'all' || eimStatusKey(v, now, isDown) === filter)
      .filter((v) => q === '' || matchesVehicleQuery(v, q))
  }, [vehicles, filter, query, now, isDown])
  // One pass tallying every bucket at once for the chip counts below, not one
  // vehicles.filter() per chip (7x the classification work for the same answer) --
  // LiveView re-renders every second on the `now` ticker, so this runs every tick.
  const statusCounts = useMemo(() => {
    const counts: Record<EimStatusKey, number> = {
      moving: 0, idle: 0, parked: 0, alert: 0, overheat: 0, charging: 0, offline: 0, breakdown: 0,
    }
    for (const v of vehicles) counts[eimStatusKey(v, now, isDown)] += 1
    return counts
  }, [vehicles, now, isDown])

  useEffect(() => {
    setLoaded(false)
    // 10s, not MBMT's 2.5s -- the poller behind this data itself only refreshes every
    // EIM_POLL_S (60s server-side default), so polling faster than that just re-fetches
    // the same snapshot.
    return pollWhileVisible(() => void refresh(), 10_000)
  }, [refresh])

  // Ground ops asked for prolonged idle/HVAC draw to be REPORTED, not just visible
  // alongside everything else -- computed client-side from the same /api/eim/live poll
  // rather than a new endpoint, since idle_since is already on every row and this is the
  // one source of truth the tiles and the map already read.
  const alerts = useMemo(
    () => vehicles
      .filter((v) => isIdleAlert(v, now))
      .sort((a, b) => (idleMinutes(b, now) ?? 0) - (idleMinutes(a, now) ?? 0)),
    [vehicles, now],
  )
  // GTI-only (isLowSoc's own gate) -- a truck below GTI_LOW_SOC_PCT risks not making it
  // to a swap station on its own charge. Naturally empty while viewing JM_BAXI alone,
  // since `vehicles` is already scoped to the site filter by fetchEimLive(site).
  const lowSocAlerts = useMemo(
    () => vehicles.filter(isLowSoc).sort((a, b) => (a.soc ?? 100) - (b.soc ?? 100)),
    [vehicles],
  )
  // Independent of status -- a vehicle can be overheated while moving, charging, or idle
  // alike (see overheat_since's own comment in useEimApi.ts), so this is computed the same
  // way as `alerts` above rather than filtered off any particular status value. ALSO
  // includes a vehicle that isn't currently live-overheating but has a cleared-today
  // episode (SPEC.md AC-5/AC-6/AC-10) -- the live feed alone would drop it the instant
  // overheat_since goes back to null.
  const overheatAlerts = useMemo(() => {
    const live = vehicles.filter((v) => isBatteryOverheatAlert(v, now))
    const liveIds = new Set(live.map((v) => v.vehicle_id))
    const clearedToday = vehicles.filter((v) =>
      !liveIds.has(v.vehicle_id)
      && (overheatEpisodesByVehicle[`${v.site}:${v.vehicle_id}`] ?? []).some((e) => e.cleared_ts != null))
    return [...live, ...clearedToday].sort((a, b) => (b.max_cell_temp ?? 0) - (a.max_cell_temp ?? 0))
  }, [vehicles, now, overheatEpisodesByVehicle])

  // idle_since is IDLE_ALERT's episode (a real boundary -- a new idle stretch always
  // needs a fresh ack); overheat_since is BATTERY_OVERHEAT's, same reasoning; LOW_SOC has
  // none, so its episode is always ''. See eim_alert_ack's own comment in db/schema_eim.sql
  // for why LOW_SOC differs here.
  const episodeFor = useCallback((v: EimVehicle, kind: EimAlertKind): string =>
    kind === 'IDLE_ALERT' ? (v.idle_since ?? '') : kind === 'BATTERY_OVERHEAT' ? (v.overheat_since ?? '') : '', [])

  const ackByKey = useMemo(() => {
    const m = new Map<string, EimAlertAck>()
    for (const a of acks) m.set(`${a.vehicle_id}:${a.kind}:${a.episode}`, a)
    return m
  }, [acks])
  const ackFor = useCallback((v: EimVehicle, kind: EimAlertKind) =>
    ackByKey.get(`${v.vehicle_id}:${kind}:${episodeFor(v, kind)}`) ?? null,
    [ackByKey, episodeFor])

  const ack = useCallback(async (v: EimVehicle, kind: EimAlertKind) => {
    const by = signedInName ?? requireSupervisor((msg, dflt) => window.prompt(msg, dflt))
    try {
      await ackEimAlert(v.site, v.vehicle_id, kind, episodeFor(v, kind), by)
      void refresh()
    } catch {
      // Silent -- the panel's own next 10s poll reconciles either way, and a failed ack
      // just leaves the alert unacknowledged (safe default: still flagged, not silently
      // marked handled).
    }
  }, [episodeFor, refresh, signedInName])

  const unack = useCallback(async (v: EimVehicle, kind: EimAlertKind) => {
    const by = signedInName ?? requireSupervisor((msg, dflt) => window.prompt(msg, dflt))
    try {
      await unackEimAlert(v.site, v.vehicle_id, kind, episodeFor(v, kind), by)
      void refresh()
    } catch {
      // Same reasoning as ack above.
    }
  }, [episodeFor, refresh, signedInName])

  // Idle alerts first (already duration-sorted), low-battery appended after (already
  // SOC-sorted) -- one unified, scrollable list for the side panel, same shape
  // Ultratech's own ALERTS tab shows (every kind together, not split by kind into
  // separate panels). Concatenated rather than interleaved: the two kinds measure
  // different things (idle minutes vs SOC%), so there is no honest single ranking
  // across both to sort by.
  const combinedAlerts = useMemo(
    () => [
      ...overheatAlerts.map((v) => ({ v, kind: 'BATTERY_OVERHEAT' as const })),
      ...alerts.map((v) => ({ v, kind: 'IDLE_ALERT' as const })),
      ...lowSocAlerts.map((v) => ({ v, kind: 'LOW_SOC' as const })),
    ],
    [overheatAlerts, alerts, lowSocAlerts],
  )

  const detailVehicle = alertDetailFor
    ? vehicles.find((vv) => vv.site === alertDetailFor.v.site && vv.vehicle_id === alertDetailFor.v.vehicle_id)
      ?? alertDetailFor.v
    : null

  return (
    <>
      <KpiStrip>
        <KpiCard label="Vehicles reporting" value={summary?.vehicles_reporting ?? '—'} tone="info" dot />
        <KpiCard
          label="Down"
          value={loaded ? breakdowns.length : '—'}
          accentColor={breakdowns.length > 0 ? EIM_STATUS_STYLE.breakdown.hex : EIM_STATUS_STYLE.moving.hex}
          dot
        />
        <KpiCard
          label="Idling now"
          // Client-computed from the same statusCounts the IDLE and FLAGGED chips below
          // render from -- NOT the backend's own summary.idling_now. That field is a
          // separate SQL count (latest-poll instant power) that silently drifted from
          // the tile grid's classification before (see f_eim_frame_info's own incident
          // history), and even reconciled, splits "idling" across two tiles (IDLE,
          // under the alert threshold, and FLAGGED, over it) that this headline number
          // is supposed to total -- a user comparing it against the IDLE chip alone saw
          // a mismatch that was really just the FLAGGED vehicles missing from the sum.
          // One source of truth for "is this vehicle idling right now" removes both
          // problems: the total is always exactly IDLE + FLAGGED, by construction.
          value={loaded ? statusCounts.idle + statusCounts.alert : '—'}
          tone={loaded && statusCounts.idle + statusCounts.alert > 0 ? 'warn' : 'ok'}
          dot
        />
        <KpiCard
          label="Charging now"
          value={summary?.charging_now ?? '—'}
          accentColor={EIM_STATUS_STYLE.charging.hex}
          dot
        />
        <KpiCard
          label={`Idle alerts (${THRESHOLD_LABEL})`}
          value={loaded ? alerts.length : '—'}
          accentColor={alerts.length > 0 ? EIM_STATUS_STYLE.alert.hex : EIM_STATUS_STYLE.moving.hex}
          dot
        />
        <KpiCard
          label="Low battery (GTI < 25%)"
          value={loaded ? lowSocAlerts.length : '—'}
          tone={lowSocAlerts.length > 0 ? 'danger' : 'ok'}
          dot
        />
        <KpiCard
          label="Idle hours today"
          value={summary ? summary.idle_hours_today.toFixed(1) : '—'}
          sub="ignition-on, not moving"
        />
        <KpiCard
          label="Idle energy today"
          value={summary ? summary.idle_energy_kwh_today.toFixed(1) : '—'}
          unit="kWh"
          tone="warn"
          sub="measured, not assumed"
        />
      </KpiStrip>

      <div className="mt-3 flex min-h-0 flex-1 gap-3">
        <div className="flex w-[340px] shrink-0 flex-col">
          <SidePanel
            tabs={[{ id: 'alerts', label: 'ALERTS', count: combinedAlerts.length }]}
            active="alerts"
            onTab={() => {}}
          >
            <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto">
              {!loaded ? (
                <EmptyState title="Loading…" />
              ) : combinedAlerts.length === 0 ? (
                <EmptyState title="No active alerts" body="Nothing idling, overheating, or low on battery right now." />
              ) : (
                combinedAlerts.map(({ v, kind }) => {
                  const acked = ackFor(v, kind)
                  const rowStyle = kind === 'IDLE_ALERT' ? EIM_STATUS_STYLE.alert
                    : kind === 'BATTERY_OVERHEAT' ? EIM_STATUS_STYLE.overheat
                    : EIM_STATUS_STYLE.breakdown
                  const detail = kind === 'IDLE_ALERT' ? `idling ${idleMinutes(v, now)}m`
                    : kind === 'BATTERY_OVERHEAT' ? `${v.max_cell_temp != null ? v.max_cell_temp.toFixed(0) : '—'}°C`
                    : `${v.soc != null ? v.soc.toFixed(0) : '—'}% SOC`
                  // IDLE_ALERT/BATTERY_OVERHEAT each have a real fired-at (idle_since/
                  // overheat_since); LOW_SOC has no such episode boundary (see useEimApi.ts),
                  // so its clock is the last reading instead -- still a timestamp, just
                  // "as of" rather than "since".
                  // BATTERY_OVERHEAT: v.overheat_since is null for a cleared-but-today
                  // episode (SPEC.md AC-5/AC-6/AC-10), so fall back to the fetched
                  // episode's own started_ts -- the most recent cleared one for this
                  // vehicle, since that's the only reason it's in overheatAlerts at all
                  // without a live overheat_since.
                  const todaysOverheatEpisodes = overheatEpisodesByVehicle[`${v.site}:${v.vehicle_id}`] ?? []
                  // Only consult a cleared-today episode when the vehicle is NOT
                  // currently live-overheating (adversarial review, HIGH -- round 2):
                  // a vehicle firing right now can ALSO have an unrelated, already-
                  // cleared episode from earlier today, and the row used to show that
                  // stale clear time alongside the live trigger regardless -- "triggered
                  // just now / cleared 4h ago" on a currently-hot vehicle. Mirrors
                  // EimAlertDetail.tsx's own matchingOverheatEpisode fix exactly: the
                  // cleared-episode fallback is ONLY for the no-live-overheat_since case
                  // this row exists for in the first place (SPEC.md AC-10).
                  const clearedOverheatEpisode = v.overheat_since
                    ? null
                    : todaysOverheatEpisodes.filter((e) => e.cleared_ts != null).at(-1) ?? null
                  const episodeStart = kind === 'IDLE_ALERT' ? v.idle_since
                    : kind === 'BATTERY_OVERHEAT' ? (v.overheat_since ?? clearedOverheatEpisode?.episode_started_ts ?? null)
                    : null
                  const firedTs = episodeStart ? new Date(episodeStart).getTime() : new Date(v.polled_ts).getTime()
                  const overheatClearedTs = kind === 'BATTERY_OVERHEAT' && clearedOverheatEpisode?.cleared_ts
                    ? new Date(clearedOverheatEpisode.cleared_ts).getTime()
                    : null
                  return (
                    <div key={`${v.site}:${v.vehicle_id}:${kind}`}>
                      <button
                        type="button"
                        onClick={() => {
                          onSite(v.site)
                          setFocusVehicle({ site: v.site, vehicleId: v.vehicle_id })
                          mapPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                          setAlertDetailFor({ v, kind })
                        }}
                        className={clsx(
                          'group grid w-full grid-cols-[3px_1fr_auto] items-start gap-2.5 rounded-lg border border-transparent',
                          'px-1.5 py-2 text-left transition-colors hover:border-[rgba(15,23,42,0.07)] hover:bg-[rgba(15,23,42,0.03)]',
                          acked ? 'opacity-50' : '',
                        )}
                      >
                        <span className="mt-0.5 h-full w-[3px] shrink-0 rounded-full" style={{ background: acked ? '#94A3B8' : rowStyle.hex }} />
                        <span className="min-w-0">
                          <span className="flex items-center gap-1.5">
                            <span className="truncate text-[10px] font-semibold uppercase tracking-[0.06em]" style={{ color: acked ? '#94A3B8' : rowStyle.text }}>
                              {kind === 'IDLE_ALERT' ? 'idle alert' : kind === 'BATTERY_OVERHEAT' ? 'battery overheat' : 'low battery'}
                            </span>
                            {acked && (
                              <span className="shrink-0 rounded-sm bg-[rgba(245,158,11,0.10)] px-1 text-[9px] font-semibold text-[#B45309]">ack</span>
                            )}
                          </span>
                          <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-[#64748B]">
                            <span className="font-mono font-bold text-[#0F172A]">{v.vehicle_id}</span>
                            <span>{SITE_LABEL[v.site]}</span>
                            <span className="ml-auto tabular-nums">{detail}</span>
                          </span>
                          {kind === 'BATTERY_OVERHEAT' ? (
                            <>
                              <span className="mt-0.5 block font-mono text-[10px] tabular-nums text-[#94A3B8]">
                                triggered {clock(firedTs)} · {ago(firedTs, now)}
                              </span>
                              <span className="mt-0.5 block font-mono text-[10px] tabular-nums text-[#94A3B8]">
                                cleared {overheatClearedTs != null ? `${clock(overheatClearedTs)} · ${ago(overheatClearedTs, now)}` : '—'}
                              </span>
                            </>
                          ) : (
                            <span className="mt-0.5 block font-mono text-[10px] tabular-nums text-[#94A3B8]">
                              {clock(firedTs)} · {ago(firedTs, now)}
                            </span>
                          )}
                        </span>
                        <ChevronRight size={13} className="mt-1 shrink-0 text-[#CBD5E1] transition-colors group-hover:text-[#64748B]" />
                      </button>
                    </div>
                  )
                })
              )}
            </div>
          </SidePanel>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
          <div ref={mapPanelRef}>
            <EimPortsMap
              vehicles={vehicles} site={site} isDown={isDown}
              onMarkBreakdown={(vSite, vehicleId) => setBreakdownFormFor({ site: vSite, vehicleId })}
              focusVehicle={focusVehicle}
            />
          </div>

          {breakdowns.length > 0 && (
            <Panel title={`Breakdowns · ${breakdowns.length}`}>
              <div className="flex flex-col gap-1.5">
                {breakdowns.map((b) => (
                  <div key={`${b.site}:${b.breakdown_id}`} className="rounded-[10px] border border-[rgba(244,63,94,0.20)] bg-[rgba(244,63,94,0.04)] px-3 py-2">
                    <div className="flex items-center gap-3">
                      <AlertOctagon size={13} className="shrink-0 text-[#9F1239]" />
                      <span className="font-mono text-[13px] font-bold tabular-nums text-[#0F172A]">{b.vehicle_id}</span>
                      <span className="text-[10px] font-medium uppercase tracking-wide text-[#94A3B8]">{SITE_LABEL[b.site]}</span>
                      <span className="text-[10px] font-medium uppercase tracking-wide text-[#9F1239]">{b.category}</span>
                      <span className="text-[11px] text-[#64748B]">{b.note || '—'}</span>
                      <span className="ml-auto text-[11px] text-[#94A3B8]">reported {ago(b.opened_ts)} ago</span>
                      <button
                        type="button"
                        onClick={() => void quickResolve(b.site, b.breakdown_id)}
                        className="rounded-full border border-[rgba(15,23,42,0.10)] px-2 py-1 text-[10px] font-semibold text-[#047857] hover:bg-[rgba(16,185,129,0.08)]"
                      >
                        Back in service
                      </button>
                    </div>
                    {b.resumed_moving_ts != null && (
                      <div className="mt-1.5 flex items-center justify-between gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
                        <span>started moving at {clock(b.resumed_moving_ts)} — resolve?</span>
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            type="button"
                            onClick={() => void dismissMoving(b.site, b.breakdown_id)}
                            className="rounded px-1.5 py-0.5 text-amber-700 hover:bg-amber-100"
                          >
                            Dismiss
                          </button>
                          <button
                            type="button"
                            onClick={() => void quickResolve(b.site, b.breakdown_id)}
                            className="rounded bg-amber-600 px-1.5 py-0.5 font-semibold text-white hover:bg-amber-700"
                          >
                            Resolve
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Panel>
          )}

          <Panel title={`Vehicles${vehicles.length ? ` · ${vehicles.length}` : ''}`} className="flex-1">
            {err ? (
              <EmptyState title="Couldn't load Ports telemetry" body={err} />
            ) : !loaded ? (
              <EmptyState title="Loading…" />
            ) : vehicles.length === 0 ? (
              <EmptyState
                title="No vehicles reporting"
                body="Either the poller hasn't run yet or the fleet-eim credentials aren't configured."
              />
            ) : (
              <>
                <div className="mb-2">
                  <SearchBox
                    value={query}
                    onChange={setQuery}
                    placeholder="Search vehicle no."
                    counter={`${filteredVehicles.length}/${vehicles.length}`}
                  />
                </div>
                <div className="mb-2">
                  <FilterChips
                    options={FLEET_FILTERS.map((f) => ({
                      ...f,
                      count: f.id === 'all' ? vehicles.length : statusCounts[f.id],
                    }))}
                    active={filter}
                    onPick={(id) => setFilter(id as 'all' | EimStatusKey)}
                  />
                </div>
                {filteredVehicles.length === 0 ? (
                  <EmptyState title="No vehicles match" body="Widen the filter or search to see more." />
                ) : (
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2">
                    {filteredVehicles.map((v) => (
                      <EimVehicleTile
                        key={`${v.site}:${v.vehicle_id}`} v={v} now={now}
                        openBreakdown={breakdownByVehicle.get(`${v.site}:${v.vehicle_id}`) ?? null}
                        selected={focusVehicle?.site === v.site && focusVehicle?.vehicleId === v.vehicle_id}
                        onSelect={() => {
                          setFocusVehicle({ site: v.site, vehicleId: v.vehicle_id })
                          mapPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                        }}
                        onReportBreakdown={() => setBreakdownFormFor({ site: v.site, vehicleId: v.vehicle_id })}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </Panel>
        </div>
      </div>

      {detailVehicle && alertDetailFor && (
        <EimAlertDetail
          v={detailVehicle}
          kind={alertDetailFor.kind}
          ack={ackFor(detailVehicle, alertDetailFor.kind)}
          onClose={() => setAlertDetailFor(null)}
          onAck={() => ack(detailVehicle, alertDetailFor.kind)}
          onUnack={() => unack(detailVehicle, alertDetailFor.kind)}
        />
      )}

      {breakdownFormFor && (
        <EimBreakdownForm
          site={breakdownFormFor.site}
          vehicleId={breakdownFormFor.vehicleId}
          existing={breakdownByVehicle.get(`${breakdownFormFor.site}:${breakdownFormFor.vehicleId}`) ?? null}
          codes={breakdownCodes[breakdownFormFor.site]}
          onClose={() => setBreakdownFormFor(null)}
          onSaved={() => void refresh()}
        />
      )}
    </>
  )
}

function IdleLeaderboard({ rows, loading }: { rows: EimIdleRow[]; loading: boolean }): JSX.Element {
  if (loading) return <EmptyState title="Loading…" />
  if (rows.length === 0) {
    return <EmptyState title="No idle time in the last 24h" body="Every polled vehicle was moving whenever it was seen." />
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="border-b border-[rgba(15,23,42,0.07)] text-left">
            <th className={clsx(EYEBROW, 'py-2 pr-3')}>Vehicle</th>
            <th className={clsx(EYEBROW, 'py-2 pr-3')}>Site</th>
            <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Episodes</th>
            <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Idle hours</th>
            <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Idle energy</th>
            <th className={clsx(EYEBROW, 'py-2 text-right')}>Last idle</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.site}:${r.vehicle_id}`} className="border-b border-[rgba(15,23,42,0.04)] last:border-0">
              <td className="py-1.5 pr-3 font-mono font-semibold tabular-nums text-[#0F172A]">{r.vehicle_id}</td>
              <td className="py-1.5 pr-3 text-[#64748B]">{SITE_LABEL[r.site]}</td>
              <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#0F172A]">{r.episode_count}</td>
              <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#0F172A]">{r.idle_hours.toFixed(1)}</td>
              <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#B45309]">{r.idle_energy_kwh.toFixed(2)} kWh</td>
              <td className="py-1.5 text-right font-mono text-[11px] tabular-nums text-[#94A3B8]">
                {new Date(r.last_idle_ts).toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function trendToBarPoints(rows: EimTrendRow[], site: EimSite | 'all'): { data: BarPoint[]; series: BarSeries[] } {
  const series: BarSeries[] = site === 'all'
    ? [
        { key: 'gti', label: 'GTI', color: SITE_COLOR.gti },
        { key: 'jm_baxi', label: 'JM Baxi', color: SITE_COLOR.jm_baxi },
      ]
    : [{ key: site, label: SITE_LABEL[site], color: SITE_COLOR[site] }]

  const byDay = new Map<string, Record<string, number>>()
  for (const r of rows) {
    const bucket = byDay.get(r.day) ?? {}
    bucket[r.site] = (bucket[r.site] ?? 0) + r.idle_energy_kwh
    byDay.set(r.day, bucket)
  }
  const data: BarPoint[] = [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, values]) => ({
      label: new Date(day).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'UTC' }),
      values,
      title: day,
    }))
  return { data, series }
}

function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
}

function fmtHours(h: number | null): string {
  return h == null ? '—' : `${h}h`
}

/** Every day-wise breakdown report in one place -- daily counts, what's open right now,
 *  downtime by fault category, repeat offenders, and the data-quality check, reading the
 *  same f_site_breakdown_* functions the Live tab's own board comes from (Phase 2,
 *  db/reports_site_breakdown.sql). site scopes the same way every other Ports report
 *  here does -- 'all' combines GTI and JM_BAXI server-side. Ported from STAR's identical
 *  panel (console/src/star/StarShell.tsx) rather than shared, same reasoning as every
 *  other STAR/Ports pairing in this codebase: each fleet's view rides its own API module. */
function BreakdownReportsPanel({ site }: { site: EimSite | 'all' }): JSX.Element {
  const [daily, setDaily] = useState<EimBreakdownReportRow<'daily'>[]>([])
  const [open, setOpenRows] = useState<EimBreakdownReportRow<'open'>[]>([])
  const [downtime, setDowntime] = useState<EimBreakdownReportRow<'downtime'>[]>([])
  const [repeat, setRepeat] = useState<EimBreakdownReportRow<'repeat'>[]>([])
  const [hygiene, setHygiene] = useState<EimBreakdownReportRow<'hygiene'>[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    const kinds: EimBreakdownReportKind[] = ['daily', 'open', 'downtime', 'repeat', 'hygiene']
    Promise.all(kinds.map((k) => fetchEimBreakdownReport(k, site)))
      .then(([d, o, dt, r, h]) => {
        setDaily(d as EimBreakdownReportRow<'daily'>[])
        setOpenRows(o as EimBreakdownReportRow<'open'>[])
        setDowntime(dt as EimBreakdownReportRow<'downtime'>[])
        setRepeat(r as EimBreakdownReportRow<'repeat'>[])
        setHygiene(h as EimBreakdownReportRow<'hygiene'>[])
      })
      .catch(() => { setDaily([]); setOpenRows([]); setDowntime([]); setRepeat([]); setHygiene([]) })
      .finally(() => setLoading(false))
  }, [site])

  // Daily rows come back one per (site, day) -- fold to one row per day when viewing
  // 'all' so the table reads as a corridor-wide total, not two interleaved sequences.
  const dailyMerged = useMemo(() => {
    if (site !== 'all') return daily
    const byDay = new Map<string, EimBreakdownReportRow<'daily'>>()
    for (const d of daily) {
      const cur = byDay.get(d.day)
      if (!cur) { byDay.set(d.day, { ...d }); continue }
      cur.opened += d.opened; cur.cleared += d.cleared; cur.updated += d.updated
      cur.vehicles_down += d.vehicles_down; cur.immobilised += d.immobilised
      cur.major += d.major; cur.minor += d.minor
    }
    return [...byDay.values()].sort((a, b) => b.day.localeCompare(a.day))
  }, [daily, site])

  const hygieneTotal = useMemo(() => hygiene.reduce((acc, h) => ({
    incidents_logged: acc.incidents_logged + h.incidents_logged,
    never_cleared: acc.never_cleared + h.never_cleared,
    open_over_7_days: acc.open_over_7_days + h.open_over_7_days,
    currently_marked_down: acc.currently_marked_down + h.currently_marked_down,
  }), { incidents_logged: 0, never_cleared: 0, open_over_7_days: 0, currently_marked_down: 0 }), [hygiene])
  const neverClearedPct = hygieneTotal.incidents_logged > 0
    ? Math.round(100 * hygieneTotal.never_cleared / hygieneTotal.incidents_logged) : null

  return (
    <div className="flex flex-1 flex-col gap-3">
      <Panel title="Breakdowns — day by day">
        {loading ? (
          <EmptyState title="Loading…" />
        ) : dailyMerged.length === 0 ? (
          <EmptyState title="No breakdowns recorded" body="Nothing has been marked down yet." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-[rgba(15,23,42,0.07)] text-left">
                  <th className={clsx(EYEBROW, 'py-2 pr-3')}>Day</th>
                  <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Opened</th>
                  <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Cleared</th>
                  <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Vehicles down</th>
                  <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Immobilised</th>
                  <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Major</th>
                  <th className={clsx(EYEBROW, 'py-2 text-right')}>Minor</th>
                </tr>
              </thead>
              <tbody>
                {dailyMerged.map((d) => (
                  <tr key={d.day} className="border-b border-[rgba(15,23,42,0.04)] last:border-0">
                    <td className="py-1.5 pr-3 font-mono text-[#0F172A]">{fmtDay(d.day)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#0F172A]">{d.opened}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#047857]">{d.cleared}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#0F172A]">{d.vehicles_down}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#9F1239]">{d.immobilised}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#B45309]">{d.major}</td>
                    <td className="py-1.5 text-right font-mono tabular-nums text-[#64748B]">{d.minor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <div className="grid gap-3 lg:grid-cols-2">
        <Panel title={`Currently down${open.length ? ` · ${open.length}` : ''}`}>
          {open.length === 0 ? (
            <EmptyState title="Nothing down" body="Every truck is in service." />
          ) : (
            <div className="flex flex-col gap-1.5">
              {open.map((o) => (
                <div key={`${o.site}:${o.breakdown_id}`} className="rounded-[10px] border border-[rgba(15,23,42,0.08)] px-3 py-2">
                  <div className="flex items-center gap-2 text-[12px]">
                    <span className="font-mono font-bold text-[#0F172A]">{o.vehicle_id}</span>
                    <span className="text-[10px] font-medium uppercase tracking-wide text-[#94A3B8]">{SITE_LABEL[o.site]}</span>
                    <span className="text-[10px] font-medium uppercase tracking-wide text-[#9F1239]">{o.category}</span>
                    <span className="ml-auto font-mono tabular-nums text-[#64748B]">{fmtHours(o.hours_down)} down</span>
                  </div>
                  {o.eta_overdue && (
                    <div className="mt-1 text-[11px] font-medium text-[#B45309]">ETA has passed</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Repeat offenders">
          {repeat.length === 0 ? (
            <EmptyState title="No repeats" body="No truck has broken down more than once." />
          ) : (
            <div className="flex flex-col gap-1.5">
              {repeat.map((r) => (
                <div key={`${r.site}:${r.vehicle_id}`} className="flex items-center gap-2 rounded-[10px] border border-[rgba(15,23,42,0.08)] px-3 py-2 text-[12px]">
                  <span className="font-mono font-bold text-[#0F172A]">{r.vehicle_id}</span>
                  <span className="text-[10px] font-medium uppercase tracking-wide text-[#94A3B8]">{SITE_LABEL[r.site]}</span>
                  <span className="text-[#64748B]">{r.incidents} incidents</span>
                  <span className="ml-auto text-[11px] text-[#94A3B8]">{r.most_common_cause ?? '—'}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel title="Downtime by fault category">
        {downtime.length === 0 ? (
          <EmptyState title="No closed incidents yet" body="Downtime can only be measured once a breakdown clears." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-[rgba(15,23,42,0.07)] text-left">
                  <th className={clsx(EYEBROW, 'py-2 pr-3')}>Category</th>
                  <th className={clsx(EYEBROW, 'py-2 pr-3')}>Site</th>
                  <th className={clsx(EYEBROW, 'py-2 pr-3')}>Severity</th>
                  <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Incidents</th>
                  <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Median</th>
                  <th className={clsx(EYEBROW, 'py-2 text-right')}>Total lost</th>
                </tr>
              </thead>
              <tbody>
                {downtime.map((d, i) => (
                  <tr key={`${d.site}-${d.category}-${d.severity}-${i}`} className="border-b border-[rgba(15,23,42,0.04)] last:border-0">
                    <td className="py-1.5 pr-3 text-[#0F172A]">{d.category}</td>
                    <td className="py-1.5 pr-3 text-[#94A3B8]">{SITE_LABEL[d.site]}</td>
                    <td className="py-1.5 pr-3 text-[#64748B]">{d.severity}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#0F172A]">{d.incidents}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#64748B]">{fmtHours(d.median_hours)}</td>
                    <td className="py-1.5 text-right font-mono tabular-nums text-[#0F172A]">{fmtHours(d.total_hours_lost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {hygieneTotal.incidents_logged > 0 && (
        <Panel title="Record quality">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <div className="font-mono text-[20px] font-bold tabular-nums text-[#0F172A]">{hygieneTotal.incidents_logged}</div>
              <div className={EYEBROW}>logged</div>
            </div>
            <div>
              <div className="font-mono text-[20px] font-bold tabular-nums text-[#0F172A]">{hygieneTotal.never_cleared}</div>
              <div className={EYEBROW}>never cleared</div>
            </div>
            <div>
              <div className="font-mono text-[20px] font-bold tabular-nums text-[#B45309]">{hygieneTotal.open_over_7_days}</div>
              <div className={EYEBROW}>open 7d+</div>
            </div>
            <div>
              <div className="font-mono text-[20px] font-bold tabular-nums text-[#0F172A]">
                {neverClearedPct != null ? `${neverClearedPct}%` : '—'}
              </div>
              <div className={EYEBROW}>never-cleared rate</div>
            </div>
          </div>
        </Panel>
      )}
    </div>
  )
}

const IDLE_ALERT_LIST_PAGE = 25

/** The drill-down the two roll-ups above cannot answer: which individual episodes make
 *  up a day's or a truck's total, newest first. Reads the `list` kind of
 *  /api/eim/idle-alerts/reports (serving/app.py), whose numbers come back UNROUNDED --
 *  unlike the daily/vehicle kinds, which round in SQL -- so the rounding is done here,
 *  to the same 1dp hours / 2dp kWh the other two tables show.
 *
 *  Deliberately unwindowed, unlike the roll-ups: this is the "show me the actual rows"
 *  view, already bounded by LIMIT/OFFSET rather than by a date range. Paging relies on
 *  the server's ordering being a total order (ORDER BY episode_started_ts DESC, site,
 *  vehicle_id) precisely so a page boundary cannot skip or repeat a row. */
function IdleAlertEpisodeList({ site }: { site: EimSite | 'all' }): JSX.Element {
  const [rows, setRows] = useState<EimIdleAlertListRow[]>([])
  const [loading, setLoading] = useState(true)
  const [offset, setOffset] = useState(0)

  // Site changes reset paging -- holding offset across a site switch would land the
  // operator on page 4 of a fleet that may only have one page.
  useEffect(() => { setOffset(0) }, [site])

  useEffect(() => {
    let live = true
    setLoading(true)
    fetchEimIdleAlertList(site, { limit: IDLE_ALERT_LIST_PAGE, offset })
      .then((r) => { if (live) setRows(r) })
      .catch(() => { if (live) setRows([]) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [site, offset])

  // A short page means there is nothing after it. Cheaper than a separate COUNT(*) over a
  // table that is never purged, and the only cost of being wrong is one empty page.
  const hasNext = rows.length === IDLE_ALERT_LIST_PAGE

  return (
    <Panel title="Idle-alert history — episodes (newest first)">
      {loading ? (
        <EmptyState title="Loading…" />
      ) : rows.length === 0 ? (
        <EmptyState
          title={offset === 0 ? 'No archived idle alerts' : 'No further episodes'}
          body={
            offset === 0
              ? 'Nothing has crossed the 20-minute threshold yet.'
              : 'You have reached the end of the history.'
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-[rgba(15,23,42,0.07)] text-left">
                <th className={clsx(EYEBROW, 'py-2 pr-3')}>Vehicle</th>
                {site === 'all' && <th className={clsx(EYEBROW, 'py-2 pr-3')}>Site</th>}
                <th className={clsx(EYEBROW, 'py-2 pr-3')}>Started</th>
                <th className={clsx(EYEBROW, 'py-2 pr-3')}>Ended</th>
                <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Hours</th>
                <th className={clsx(EYEBROW, 'py-2 text-right')}>Energy (kWh)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={`${r.site}-${r.vehicle_id}-${r.episode_started_ts}`}
                  className="border-b border-[rgba(15,23,42,0.04)] last:border-0"
                >
                  <td className="py-1.5 pr-3 font-mono font-semibold tabular-nums text-[#0F172A]">{r.vehicle_id}</td>
                  {site === 'all' && <td className="py-1.5 pr-3 text-[#94A3B8]">{SITE_LABEL[r.site]}</td>}
                  <td className="py-1.5 pr-3 font-mono tabular-nums text-[#64748B]">{dateTime(r.episode_started_ts)}</td>
                  <td className="py-1.5 pr-3 font-mono tabular-nums text-[#64748B]">{dateTime(r.episode_ended_ts)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#0F172A]">
                    {(r.duration_sec / 3600).toFixed(1)}
                  </td>
                  <td className="py-1.5 text-right font-mono tabular-nums text-[#B45309]">
                    {r.idle_energy_kwh.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(offset > 0 || hasNext) && (
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset((o) => Math.max(0, o - IDLE_ALERT_LIST_PAGE))}
            className="rounded-lg border border-[rgba(15,23,42,0.10)] px-2.5 py-1 text-[11px] font-semibold text-[#64748B] transition-colors hover:border-[rgba(15,23,42,0.22)] hover:text-[#0F172A] disabled:cursor-not-allowed disabled:opacity-40"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={!hasNext}
            onClick={() => setOffset((o) => o + IDLE_ALERT_LIST_PAGE)}
            className="rounded-lg border border-[rgba(15,23,42,0.10)] px-2.5 py-1 text-[11px] font-semibold text-[#64748B] transition-colors hover:border-[rgba(15,23,42,0.22)] hover:text-[#0F172A] disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
          <span className="ml-auto font-mono text-[11px] tabular-nums text-[#94A3B8]">
            {rows.length === 0 ? '—' : `${offset + 1}–${offset + rows.length}`}
          </span>
        </div>
      )}
    </Panel>
  )
}

// Windowed the same way the "Idle energy trend -- last 14 days" panel above it is, and
// for the same reason: eim_idle_alert_episode is never purged, so an unwindowed report
// is a full scan that grows forever. 30 rather than 14 because an alert-crossing episode
// is a much rarer event than a day with any idle energy at all -- a fortnight of a quiet
// fleet can be legitimately empty, which reads as "broken" rather than "good".
const IDLE_ALERT_HISTORY_DAYS = 30

/** Persisted alert-episode history (eim_idle_alert_episode, archived hourly --
 *  docs/superpowers/specs/2026-10-12-eim-idle-alert-history-design.md), distinct from
 *  the live 24h "Idle / HVAC draw" panel (IdleLeaderboard, trailing-24h snapshot) and
 *  the daily-summary trend chart above (day-level kWh total only): this is the only
 *  place that shows individual past ALERT episodes (20min+, IDLE_ALERT_THRESHOLD_MIN)
 *  rolled up by day and by vehicle. "Resolved" here means the episode ended, not that
 *  anyone acknowledged it -- unrelated to eim_alert_ack/eim_alert_action. */
function IdleAlertHistoryPanel({ site }: { site: EimSite | 'all' }): JSX.Element {
  const [daily, setDaily] = useState<EimIdleAlertDailyRow[]>([])
  const [vehicle, setVehicle] = useState<EimIdleAlertVehicleRow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    Promise.all([
      fetchEimIdleAlertReport('daily', site, { days: IDLE_ALERT_HISTORY_DAYS }),
      fetchEimIdleAlertReport('vehicle', site, { days: IDLE_ALERT_HISTORY_DAYS }),
    ])
      .then(([d, v]) => { setDaily(d); setVehicle(v) })
      .catch(() => { setDaily([]); setVehicle([]) })
      .finally(() => setLoading(false))
  }, [site])

  // Daily rows come back one per (site, day), so 'all' would otherwise render two rows
  // both labelled e.g. "02 Jun" with different numbers and no column telling them apart.
  // Fold to one row per day, exactly as BreakdownReportsPanel's own dailyMerged above
  // does for the same reason. Re-rounded after summing because the server already
  // rounded each site's figure (1dp hours, 2dp kWh) -- this sums those rounded values
  // rather than re-deriving from the raw episodes, so float addition can leave a
  // 0.30000000000000004 behind.
  const dailyMerged = useMemo(() => {
    if (site !== 'all') return daily
    const byDay = new Map<string, EimIdleAlertDailyRow>()
    for (const d of daily) {
      const cur = byDay.get(d.day)
      if (!cur) { byDay.set(d.day, { ...d }); continue }
      cur.episode_count += d.episode_count
      cur.total_hours = Math.round((cur.total_hours + d.total_hours) * 10) / 10
      cur.total_energy_kwh = Math.round((cur.total_energy_kwh + d.total_energy_kwh) * 100) / 100
    }
    return [...byDay.values()].sort((a, b) => b.day.localeCompare(a.day))
  }, [daily, site])

  return (
    <div className="flex flex-1 flex-col gap-3">
      <Panel title={`Idle-alert history — by day (last ${IDLE_ALERT_HISTORY_DAYS} days)`}>
        {loading ? (
          <EmptyState title="Loading…" />
        ) : dailyMerged.length === 0 ? (
          <EmptyState title="No archived idle alerts" body="Nothing has crossed the 20-minute threshold yet." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-[rgba(15,23,42,0.07)] text-left">
                  <th className={clsx(EYEBROW, 'py-2 pr-3')}>Day</th>
                  <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Episodes</th>
                  <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Total hours</th>
                  <th className={clsx(EYEBROW, 'py-2 text-right')}>Total energy (kWh)</th>
                </tr>
              </thead>
              <tbody>
                {dailyMerged.map((d) => (
                  <tr key={d.day} className="border-b border-[rgba(15,23,42,0.04)] last:border-0">
                    <td className="py-1.5 pr-3 font-mono text-[#0F172A]">{fmtDay(d.day)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#0F172A]">{d.episode_count}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#0F172A]">{d.total_hours.toFixed(1)}</td>
                    <td className="py-1.5 text-right font-mono tabular-nums text-[#B45309]">{d.total_energy_kwh.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <Panel title={`Idle-alert history — by vehicle (last ${IDLE_ALERT_HISTORY_DAYS} days)`}>
        {loading ? (
          <EmptyState title="Loading…" />
        ) : vehicle.length === 0 ? (
          <EmptyState title="No archived idle alerts" body="Nothing has crossed the 20-minute threshold yet." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-[rgba(15,23,42,0.07)] text-left">
                  <th className={clsx(EYEBROW, 'py-2 pr-3')}>Vehicle</th>
                  {/* NOT merged like the by-day table above: a truck belongs to exactly
                      one site, so two rows sharing a vehicle_id under gti and jm_baxi are
                      genuinely different trucks, not one double-counted. They need
                      telling apart, not adding up -- hence a Site column, same
                      SITE_LABEL convention as BreakdownReportsPanel's downtime table. */}
                  <th className={clsx(EYEBROW, 'py-2 pr-3')}>Site</th>
                  <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Episodes</th>
                  <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Total hours</th>
                  <th className={clsx(EYEBROW, 'py-2 text-right')}>Total energy (kWh)</th>
                </tr>
              </thead>
              <tbody>
                {vehicle.map((v) => (
                  <tr key={`${v.site}-${v.vehicle_id}`} className="border-b border-[rgba(15,23,42,0.04)] last:border-0">
                    <td className="py-1.5 pr-3 font-mono font-semibold tabular-nums text-[#0F172A]">{v.vehicle_id}</td>
                    <td className="py-1.5 pr-3 text-[#94A3B8]">{SITE_LABEL[v.site]}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#0F172A]">{v.episode_count}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#0F172A]">{v.total_hours.toFixed(1)}</td>
                    <td className="py-1.5 text-right font-mono tabular-nums text-[#B45309]">{v.total_energy_kwh.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <IdleAlertEpisodeList site={site} />
    </div>
  )
}

const OVERHEAT_ALERT_LIST_PAGE = 25

// Preset time-interval chips, same "trailing window of days" convention
// IDLE_ALERT_HISTORY_DAYS already uses -- null means all time.
const OVERHEAT_INTERVAL_OPTIONS: { label: string; days: number | null }[] = [
  { label: 'All time', days: null },
  { label: 'Last 24h', days: 1 },
  { label: 'Last 7d', days: 7 },
  { label: 'Last 30d', days: 30 },
]

/** The drill-down the two roll-ups below cannot answer: which individual episodes make
 *  up a day's or a truck's total, newest first. Reads the `list` kind of
 *  /api/eim/overheat-alerts/reports (serving/app.py). Unlike IdleAlertEpisodeList, this
 *  one also exposes a vehicle-number search and a time-interval filter directly in the
 *  UI -- the backend route has always accepted vehicle_id/from_ts/to_ts (shared with the
 *  idle-alert list), nothing here is a new API shape. */
function OverheatAlertEpisodeList({ site }: { site: EimSite | 'all' }): JSX.Element {
  const [rows, setRows] = useState<EimOverheatAlertListRow[]>([])
  const [loading, setLoading] = useState(true)
  const [offset, setOffset] = useState(0)
  const [vehicleQuery, setVehicleQuery] = useState('')
  const [intervalDays, setIntervalDays] = useState<number | null>(null)

  // Computed once per interval selection, not per render/offset change -- recomputing
  // from Date.now() on every fetch (including a page turn) let the window's old edge
  // drift forward a few seconds between pages, which could skip a boundary row.
  const fromTs = useMemo(
    () => (intervalDays == null ? undefined : new Date(Date.now() - intervalDays * 86_400_000).toISOString()),
    [intervalDays],
  )

  // Site/filter changes reset paging -- holding offset across a filter change would
  // land the operator on a page number that may not exist for the new filter.
  useEffect(() => { setOffset(0) }, [site, vehicleQuery, fromTs])

  useEffect(() => {
    let live = true
    setLoading(true)
    fetchEimOverheatAlertList(site, {
      vehicleId: vehicleQuery.trim() || undefined,
      fromTs,
      limit: OVERHEAT_ALERT_LIST_PAGE,
      offset,
    })
      .then((r) => { if (live) setRows(r) })
      .catch(() => { if (live) setRows([]) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [site, vehicleQuery, fromTs, offset])

  // A short page means there is nothing after it. Cheaper than a separate COUNT(*) over a
  // table that is never purged, and the only cost of being wrong is one empty page.
  const hasNext = rows.length === OVERHEAT_ALERT_LIST_PAGE

  return (
    <Panel title="Battery-overheat history — episodes (newest first)">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="w-56">
          <SearchBox value={vehicleQuery} onChange={setVehicleQuery} placeholder="Vehicle number…" />
        </div>
        <div className="flex items-center gap-1.5">
          {OVERHEAT_INTERVAL_OPTIONS.map((opt) => (
            <button
              key={opt.label}
              type="button"
              onClick={() => setIntervalDays(opt.days)}
              className={clsx(
                'rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors',
                intervalDays === opt.days
                  ? 'border-[#2563EB] bg-[rgba(37,99,235,0.08)] text-[#2563EB]'
                  : 'border-[rgba(15,23,42,0.10)] text-[#64748B] hover:border-[rgba(15,23,42,0.22)] hover:text-[#0F172A]',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <EmptyState title="Loading…" />
      ) : rows.length === 0 ? (
        <EmptyState
          title={offset === 0 ? 'No archived battery-overheat alerts' : 'No further episodes'}
          body={
            offset === 0
              ? 'Nothing has crossed the 45C / 5-minute threshold for this filter yet.'
              : 'You have reached the end of the history.'
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-[rgba(15,23,42,0.07)] text-left">
                <th className={clsx(EYEBROW, 'py-2 pr-3')}>Vehicle</th>
                {site === 'all' && <th className={clsx(EYEBROW, 'py-2 pr-3')}>Site</th>}
                <th className={clsx(EYEBROW, 'py-2 pr-3')}>Started</th>
                <th className={clsx(EYEBROW, 'py-2 pr-3')}>Ended</th>
                <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Duration</th>
                <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Max temp (C)</th>
                <th className={clsx(EYEBROW, 'py-2')}>Vehicle state</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={`${r.site}-${r.vehicle_id}-${r.episode_started_ts}`}
                  className="border-b border-[rgba(15,23,42,0.04)] last:border-0"
                >
                  <td className="py-1.5 pr-3 font-mono font-semibold tabular-nums text-[#0F172A]">{r.vehicle_id}</td>
                  {site === 'all' && <td className="py-1.5 pr-3 text-[#94A3B8]">{SITE_LABEL[r.site]}</td>}
                  <td className="py-1.5 pr-3 font-mono tabular-nums text-[#64748B]">{dateTime(r.episode_started_ts)}</td>
                  <td className="py-1.5 pr-3 font-mono tabular-nums text-[#64748B]">{dateTime(r.episode_ended_ts)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#0F172A]">
                    {Math.round(r.duration_sec / 60)}m
                  </td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#9F1239]">
                    {r.max_temp_c.toFixed(1)}
                  </td>
                  <td className="py-1.5 text-[#475569]">{r.vehicle_status ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(offset > 0 || hasNext) && (
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset((o) => Math.max(0, o - OVERHEAT_ALERT_LIST_PAGE))}
            className="rounded-lg border border-[rgba(15,23,42,0.10)] px-2.5 py-1 text-[11px] font-semibold text-[#64748B] transition-colors hover:border-[rgba(15,23,42,0.22)] hover:text-[#0F172A] disabled:cursor-not-allowed disabled:opacity-40"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={!hasNext}
            onClick={() => setOffset((o) => o + OVERHEAT_ALERT_LIST_PAGE)}
            className="rounded-lg border border-[rgba(15,23,42,0.10)] px-2.5 py-1 text-[11px] font-semibold text-[#64748B] transition-colors hover:border-[rgba(15,23,42,0.22)] hover:text-[#0F172A] disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
          <span className="ml-auto font-mono text-[11px] tabular-nums text-[#94A3B8]">
            {rows.length === 0 ? '—' : `${offset + 1}–${offset + rows.length}`}
          </span>
        </div>
      )}
    </Panel>
  )
}

// Windowed the same way IDLE_ALERT_HISTORY_DAYS is, and for the same reason:
// eim_overheat_alert_episode is never purged, so an unwindowed report is a full scan
// that grows forever. 30 rather than 14 for the same reasoning IDLE_ALERT_HISTORY_DAYS
// documents: an alert-crossing episode is a much rarer event than a day with any idle
// energy at all.
const OVERHEAT_ALERT_HISTORY_DAYS = 30

/** Persisted alert-episode history (eim_overheat_alert_episode, archived hourly --
 *  mirrors docs/superpowers/specs/2026-10-12-eim-idle-alert-history-design.md),
 *  structurally identical to IdleAlertHistoryPanel above: a by-day roll-up, a
 *  by-vehicle leaderboard, and a searchable/paginated drill-down list. "Resolved"
 *  here means the reading dropped back under 45C, not that an operator acknowledged
 *  it -- unrelated to eim_alert_ack/eim_alert_action. */
function OverheatAlertHistoryPanel({ site }: { site: EimSite | 'all' }): JSX.Element {
  const [daily, setDaily] = useState<EimOverheatAlertDailyRow[]>([])
  const [vehicle, setVehicle] = useState<EimOverheatAlertVehicleRow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    Promise.all([
      fetchEimOverheatAlertReport('daily', site, { days: OVERHEAT_ALERT_HISTORY_DAYS }),
      fetchEimOverheatAlertReport('vehicle', site, { days: OVERHEAT_ALERT_HISTORY_DAYS }),
    ])
      .then(([d, v]) => { setDaily(d); setVehicle(v) })
      .catch(() => { setDaily([]); setVehicle([]) })
      .finally(() => setLoading(false))
  }, [site])

  // Daily rows come back one per (site, day) -- fold to one row per day for 'all',
  // same reasoning IdleAlertHistoryPanel's own dailyMerged documents. max_temp_c takes
  // the max across sites for that day, not a sum (a temperature is never additive).
  const dailyMerged = useMemo(() => {
    if (site !== 'all') return daily
    const byDay = new Map<string, EimOverheatAlertDailyRow>()
    for (const d of daily) {
      const cur = byDay.get(d.day)
      if (!cur) { byDay.set(d.day, { ...d }); continue }
      cur.episode_count += d.episode_count
      cur.total_hours = Math.round((cur.total_hours + d.total_hours) * 10) / 10
      cur.max_temp_c = Math.max(cur.max_temp_c, d.max_temp_c)
    }
    return [...byDay.values()].sort((a, b) => b.day.localeCompare(a.day))
  }, [daily, site])

  return (
    <div className="flex flex-1 flex-col gap-3">
      <Panel title={`Battery-overheat history — by day (last ${OVERHEAT_ALERT_HISTORY_DAYS} days)`}>
        {loading ? (
          <EmptyState title="Loading…" />
        ) : dailyMerged.length === 0 ? (
          <EmptyState title="No archived battery-overheat alerts" body="Nothing has crossed the 45C / 5-minute threshold yet." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-[rgba(15,23,42,0.07)] text-left">
                  <th className={clsx(EYEBROW, 'py-2 pr-3')}>Day</th>
                  <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Episodes</th>
                  <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Total hours</th>
                  <th className={clsx(EYEBROW, 'py-2 text-right')}>Max temp (C)</th>
                </tr>
              </thead>
              <tbody>
                {dailyMerged.map((d) => (
                  <tr key={d.day} className="border-b border-[rgba(15,23,42,0.04)] last:border-0">
                    <td className="py-1.5 pr-3 font-mono text-[#0F172A]">{fmtDay(d.day)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#0F172A]">{d.episode_count}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#0F172A]">{d.total_hours.toFixed(1)}</td>
                    <td className="py-1.5 text-right font-mono tabular-nums text-[#9F1239]">{d.max_temp_c.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <Panel title={`Battery-overheat history — by vehicle (last ${OVERHEAT_ALERT_HISTORY_DAYS} days)`}>
        {loading ? (
          <EmptyState title="Loading…" />
        ) : vehicle.length === 0 ? (
          <EmptyState title="No archived battery-overheat alerts" body="Nothing has crossed the 45C / 5-minute threshold yet." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-[rgba(15,23,42,0.07)] text-left">
                  <th className={clsx(EYEBROW, 'py-2 pr-3')}>Vehicle</th>
                  {/* NOT merged like the by-day table above: a truck belongs to exactly
                      one site, same reasoning IdleAlertHistoryPanel's own vehicle table
                      documents. */}
                  <th className={clsx(EYEBROW, 'py-2 pr-3')}>Site</th>
                  <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Episodes</th>
                  <th className={clsx(EYEBROW, 'py-2 pr-3 text-right')}>Total hours</th>
                  <th className={clsx(EYEBROW, 'py-2 text-right')}>Max temp (C)</th>
                </tr>
              </thead>
              <tbody>
                {vehicle.map((v) => (
                  <tr key={`${v.site}-${v.vehicle_id}`} className="border-b border-[rgba(15,23,42,0.04)] last:border-0">
                    <td className="py-1.5 pr-3 font-mono font-semibold tabular-nums text-[#0F172A]">{v.vehicle_id}</td>
                    <td className="py-1.5 pr-3 text-[#94A3B8]">{SITE_LABEL[v.site]}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#0F172A]">{v.episode_count}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-[#0F172A]">{v.total_hours.toFixed(1)}</td>
                    <td className="py-1.5 text-right font-mono tabular-nums text-[#9F1239]">{v.max_temp_c.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <OverheatAlertEpisodeList site={site} />
    </div>
  )
}

function ReportsView({ site }: { site: EimSite | 'all' }): JSX.Element {
  const [idleRows, setIdleRows] = useState<EimIdleRow[]>([])
  const [idleLoading, setIdleLoading] = useState(true)
  const [trendRows, setTrendRows] = useState<EimTrendRow[]>([])
  const [trendLoading, setTrendLoading] = useState(true)

  useEffect(() => {
    setIdleLoading(true)
    const load = () => {
      fetchEimIdleEpisodes(site).then(setIdleRows).catch(() => setIdleRows([])).finally(() => setIdleLoading(false))
    }
    return pollWhileVisible(load, 30_000)
  }, [site])

  useEffect(() => {
    setTrendLoading(true)
    fetchEimTrend(site, 14).then(setTrendRows).catch(() => setTrendRows([])).finally(() => setTrendLoading(false))
  }, [site])

  const { data, series } = useMemo(() => trendToBarPoints(trendRows, site), [trendRows, site])

  return (
    <div className="flex flex-1 flex-col gap-3">
      <BreakdownReportsPanel site={site} />
      <Panel title="Idle / HVAC draw — trailing 24h, ranked by energy">
        <IdleLeaderboard rows={idleRows} loading={idleLoading} />
      </Panel>
      <Panel title="Idle energy trend — last 14 days">
        {trendLoading ? (
          <EmptyState title="Loading…" />
        ) : trendRows.length === 0 ? (
          <EmptyState
            title="No trend data yet"
            body="The daily rollup runs once at 00:05 IST (db/rollup_eim_daily.sql). Check back after its first run."
          />
        ) : (
          <BarChart data={data} series={series} height={140} formatValue={(v) => `${v.toFixed(1)} kWh`} />
        )}
      </Panel>
      <IdleAlertHistoryPanel site={site} />
      <OverheatAlertHistoryPanel site={site} />
    </div>
  )
}

/**
 * EIM command centre: GTI and JM_BAXI port-trucking telemetry (dwar.eim.digital). No
 * TIMS register and no geofence corridor for these two sites -- everything here reads
 * straight off eim_vehicle_telemetry / v_eim_idle_episodes / eim_vehicle_daily_summary
 * (db/schema_eim.sql, db/reports_eim.sql) via serving/app.py's /api/eim/* endpoints.
 */
export function EimShell(): JSX.Element {
  const navigate = useNavigate()
  const { tab } = useParams()
  const view = tab === 'reports' ? 'reports' : 'live'
  const session = useSession()
  const userLabel = sessionDisplayName(session)
  const [site, setSite] = useState<EimSite | 'all'>('all')
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  return (
    <div className="flex h-full flex-col overflow-hidden bg-[#E9EEF5]">
      <div className="shrink-0 px-3 pt-3">
        <TopBar
          clock={new Date(now).toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })}
          live
          user={userLabel}
          fleets={fleetPills(true)}
          activeFleet="eim"
          onFleet={(id) => navigate(pathFor(id as FleetId, defaultSurfaceFor(id as FleetId)))}
          nav={surfacePills('eim')}
          activeNav={view}
          onNav={(id) => navigate(pathFor('eim', id === 'reports' ? 'reports' : 'live'))}
          onSettings={() => navigate('/settings')}
        />
      </div>

      <div className="shrink-0 flex items-end justify-between gap-3 px-3 pt-3">
        <div>
          <p className={EYEBROW}>Port trucking · GTI &amp; JM Baxi</p>
          <h1 className="font-display text-[22px] font-semibold tracking-tight text-[#0F172A]">
            Ports command centre
          </h1>
        </div>
        <SiteToggle value={site} onChange={setSite} />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {view === 'live' ? <LiveView site={site} onSite={setSite} /> : <ReportsView site={site} />}
      </div>
    </div>
  )
}
